// src/components/DBIConfigManagement.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Loader2, RefreshCw } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Card from './Card';
import { useAuth } from '../contexts/AuthContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';



const DBIConfigManagement = () => {
  const [configData, setConfigData] = useState({
    Model: '',
    Intent_Detection_Model: '',
    Multi_Table_Review_Model: '',
    Explanation_Model: '',
    Preference_Learning_Model: '',
    Big_Bro_Model: '',
    Post_NLQ_Model: '',
    Personalized: false,
    enableMatching: false,
    enableIntentDetection: false,
    enableMultiTableReview: false,
    EnableBigBro: false,
    enableSanitization: false,
    enableInferredContext: false,
    enablePostNLQ: false,
    enableSQLEdit: false,
    sqlEditMinChars: 400,
    enableSimilarQueries: false,
    SimilarQueries_Model: '',
    similarQueriesMaxResults: 3,
    initialQueryThreshold: 5,
    retriggerThreshold: 3,
    historyQueryCount: 20,
    learningScheduleMinutes: 500,
    MaxRows: 500000,
    Support: [],
    SprayEnabled: false,
    SprayModelsCount: 2,
    SprayModelsPriority: [],
    demoMode: false
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [isTriggering, setIsTriggering] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true);
        
        // Fetch Models document
        const modelsDoc = await getDoc(doc(db, 'DBI Configuration', 'Models'));
        if (modelsDoc.exists()) {
          const modelsData = modelsDoc.data();
          // Transform the models data into a format for the dropdown
          const modelsList = Object.entries(modelsData).map(([name, details]) => ({
            name,
            ...details
          }));
          setAvailableModels(modelsList);
        }
  
        // Fetch Config document
        const configDoc = await getDoc(doc(db, 'DBI Configuration', 'Config'));
        if (configDoc.exists()) {
          const configDataFromDb = configDoc.data();
          
          setConfigData({
            Model: configDataFromDb.Model || '',
            Intent_Detection_Model: configDataFromDb.Intent_Detection_Model || '',
            Multi_Table_Review_Model: configDataFromDb.Multi_Table_Review_Model || '',
            Explanation_Model: configDataFromDb.Explanation_Model || '',
            Preference_Learning_Model: configDataFromDb.Preference_Learning_Model || '',
            Big_Bro_Model: configDataFromDb.Big_Bro_Model || '',
            Post_NLQ_Model: configDataFromDb.Post_NLQ_Model || '',
            Personalized: Boolean(configDataFromDb.Personalized),
            enableMatching: Boolean(configDataFromDb.enableMatching),
            enableIntentDetection: Boolean(configDataFromDb.enableIntentDetection),
            enableMultiTableReview: Boolean(configDataFromDb.enableMultiTableReview),
            EnableBigBro: Boolean(configDataFromDb.EnableBigBro),
            enableSanitization: Boolean(configDataFromDb.enableSanitization),
            enableInferredContext: Boolean(configDataFromDb.enableInferredContext),
            enablePostNLQ: Boolean(configDataFromDb.enablePostNLQ),
            enableSQLEdit: Boolean(configDataFromDb.enableSQLEdit),  // New field
            sqlEditMinChars: configDataFromDb.sqlEditMinChars || 400,
            enableSimilarQueries: Boolean(configDataFromDb.enableSimilarQueries),
            SimilarQueries_Model: configDataFromDb.SimilarQueries_Model || '',
            similarQueriesMaxResults: configDataFromDb.similarQueriesMaxResults || 3,
            initialQueryThreshold: configDataFromDb.initialQueryThreshold || 5,
            retriggerThreshold: configDataFromDb.retriggerThreshold || 3,
            historyQueryCount: configDataFromDb.historyQueryCount || 20,
            learningScheduleMinutes: configDataFromDb.learningScheduleMinutes || 500,
            MaxRows: configDataFromDb.MaxRows || 500000,
            Support: configDataFromDb.Support || [],
            SprayEnabled: Boolean(configDataFromDb.SprayEnabled),
            SprayModelsCount: configDataFromDb.SprayModelsCount || 2,
            SprayModelsPriority: configDataFromDb.SprayModelsPriority || []
          });
        }
  
        // Fetch core settings for demo mode
        const coreDoc = await getDoc(doc(db, 'appConfiguration', 'core'));
        if (coreDoc.exists()) {
          const coreData = coreDoc.data();
          setConfigData(prev => ({
            ...prev,
            demoMode: Boolean(coreData.demoMode)
          }));
        }
  
      } catch (err) {
        console.error('Error fetching configuration:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!configData.enableMultiTableReview && configData.SprayEnabled) {
      handleConfigChange('SprayEnabled', false);
    }
  }, [configData.enableMultiTableReview]);

  const handleConfigChange = async (field, value, document = 'Config') => {
    try {
      setSaveStatus({ type: 'loading', message: `Updating ${field}...` });
      
      if (document === 'core') {
        await updateDoc(doc(db, 'appConfiguration', 'core'), {
          [field]: value
        });
      } else {
        await updateDoc(doc(db, 'DBI Configuration', document), {
          [field]: value
        });
      }
  
      setConfigData(prev => ({
        ...prev,
        [field]: value
      }));
  
      setSaveStatus({ type: 'success', message: `${field} updated successfully!` });
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setSaveStatus({ type: 'error', message: `Failed to update ${field}` });
    } finally {
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    }
  };

  const handleSupportEmailsChange = async (emails) => {
    try {
      setSaveStatus({ type: 'loading', message: 'Updating support emails...' });
      
      await updateDoc(doc(db, 'DBI Configuration', 'Config'), {
        Support: emails
      });

      setConfigData(prev => ({
        ...prev,
        Support: emails
      }));

      setSaveStatus({ type: 'success', message: 'Support emails updated successfully!' });
    } catch (err) {
      console.error('Error updating support emails:', err);
      setSaveStatus({ type: 'error', message: 'Failed to update support emails' });
    } finally {
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    }
  };

  const handleModelPriorityReorder = (startIndex, endIndex) => {
    try {
      setSaveStatus({ type: 'loading', message: 'Updating model priority...' });
      
      const result = Array.from(configData.SprayModelsPriority);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      
      handleConfigChange('SprayModelsPriority', result);
    } catch (err) {
      console.error('Error reordering models:', err);
      setSaveStatus({ type: 'error', message: 'Failed to update model priority' });
    }
  };

  const handleDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }
  
    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    
    // Don't update if position didn't change
    if (startIndex === endIndex) {
      return;
    }
  
    const newPriority = Array.from(configData.SprayModelsPriority);
    const [removed] = newPriority.splice(startIndex, 1);
    newPriority.splice(endIndex, 0, removed);
    
    handleConfigChange('SprayModelsPriority', newPriority);
  };

  const handleTriggerValidatedQueriesIndex = async () => {
    try {
      setIsTriggering(true);
      setSaveStatus({ type: 'loading', message: 'Triggering validated queries index build...' });
      
      const functions = getFunctions();
      const triggerFunction = httpsCallable(functions, 'triggerValidatedQueriesIndex');
      
      const result = await triggerFunction();
      
      if (result.data.success) {
        setSaveStatus({ type: 'success', message: 'Validated queries index build triggered successfully!' });
      } else {
        setSaveStatus({ type: 'error', message: 'Failed to trigger index build' });
      }
    } catch (error) {
      console.error('Error triggering validated queries index:', error);
      setSaveStatus({ type: 'error', message: `Error: ${error.message}` });
    } finally {
      setIsTriggering(false);
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading configuration: {error}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        {saveStatus.message && (
          <div className={`px-4 py-2 rounded ${
            saveStatus.type === 'success' ? 'bg-green-100 text-green-700' :
            saveStatus.type === 'error' ? 'bg-red-100 text-red-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {saveStatus.message}
          </div>
        )}
      </div>
      
      <Card className="h-full">
        <div className="p-4">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Configuration</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Query Model
                </label>
                <select
                  value={configData.Model}
                  onChange={(e) => handleConfigChange('Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Multi-Table Review Model
                </label>
                <select
                  value={configData.Multi_Table_Review_Model}
                  onChange={(e) => handleConfigChange('Multi_Table_Review_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Intent Detection Model
                </label>
                <select
                  value={configData.Intent_Detection_Model}
                  onChange={(e) => handleConfigChange('Intent_Detection_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Explanation Model
                </label>
                <select
                  value={configData.Explanation_Model}
                  onChange={(e) => handleConfigChange('Explanation_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preference Learning Model
                </label>
                <select
                  value={configData.Preference_Learning_Model}
                  onChange={(e) => handleConfigChange('Preference_Learning_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Big Bro Analysis Model (must be OpenAI)
                </label>
                <select
                  value={configData.Big_Bro_Model}
                  onChange={(e) => handleConfigChange('Big_Bro_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Model used for analyzing queries and providing user guidance
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post-NLQ Model
                </label>
                <select
                  value={configData.Post_NLQ_Model}
                  onChange={(e) => handleConfigChange('Post_NLQ_Model', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a model</option>
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name} ({model.Platform} - {model.Model})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Model used for post-NLQ processing when enabled
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.Personalized}
                  onChange={(e) => handleConfigChange('Personalized', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Personalization
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableIntentDetection}
                  onChange={(e) => handleConfigChange('enableIntentDetection', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Intent Detection
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableMultiTableReview}
                  onChange={(e) => handleConfigChange('enableMultiTableReview', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Multi-Table Review
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.EnableBigBro}
                  onChange={(e) => handleConfigChange('EnableBigBro', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Big Bro Analysis
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableMatching}
                  onChange={(e) => handleConfigChange('enableMatching', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Matching
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableSanitization}
                  onChange={(e) => handleConfigChange('enableSanitization', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Sanitization
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableInferredContext}
                  onChange={(e) => handleConfigChange('enableInferredContext', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Inferred Context
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableSQLEdit}
                  onChange={(e) => handleConfigChange('enableSQLEdit', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable SQL Edit
                </label>
                <span className="text-xs text-gray-500 ml-2">
                  (Allow LLMs to edit generated SQL queries)
                </span>
              </div>

              {configData.enableSQLEdit && (
                <div className="ml-8 mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum SQL Length for Edit Option
                  </label>
                  <input
                    type="number"
                    min="100"
                    max="10000"
                    step="100"
                    value={configData.sqlEditMinChars}
                    onChange={(e) => handleConfigChange('sqlEditMinChars', parseInt(e.target.value, 10))}
                    className="w-64 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    SQL queries with more than {configData.sqlEditMinChars} characters will show edit option
                  </p>
                </div>
              )}

<div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enablePostNLQ}
                  onChange={(e) => handleConfigChange('enablePostNLQ', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Post-NLQ Processing
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={configData.enableSimilarQueries}
                  onChange={(e) => handleConfigChange('enableSimilarQueries', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">
                  Enable Similar Queries
                </label>
              </div>

              {configData.enableSimilarQueries && (
                <>
                  <div className="ml-8 mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Similar Queries Model
                    </label>
                    <select
                      value={configData.SimilarQueries_Model}
                      onChange={(e) => handleConfigChange('SimilarQueries_Model', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a model</option>
                      {availableModels.map((model) => (
                        <option key={model.name} value={model.name}>
                          {model.name} ({model.Platform} - {model.Model})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Model used for finding similar validated queries
                    </p>
                  </div>

                  <div className="ml-8 mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Similar Queries Results
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={configData.similarQueriesMaxResults}
                      onChange={(e) => handleConfigChange('similarQueriesMaxResults', parseInt(e.target.value, 10))}
                      className="w-64 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum number of similar queries to return (1-10)
                    </p>
                  </div>

                  <div className="ml-8 mt-4">
                    <button
                      onClick={handleTriggerValidatedQueriesIndex}
                      disabled={isTriggering}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isTriggering ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Building Index...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Build Validated Queries Index
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      Manually trigger the validated queries index build (for testing)
                    </p>
                  </div>
                </>
              )}

              {/* SPRAY CONFIGURATION UI */}
              <div className="border-t pt-4 mb-4">
                <h4 className="text-md font-medium mb-3">Spray Configuration</h4>
                
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={configData.SprayEnabled}
                    onChange={(e) => handleConfigChange('SprayEnabled', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={!configData.enableMultiTableReview}
                  />
                  <label className={`text-sm font-medium ${!configData.enableMultiTableReview ? 'text-gray-400' : 'text-gray-700'}`}>
                    Enable Spray (Run Multiple Models in Parallel)
                  </label>
                  {!configData.enableMultiTableReview && (
                    <span className="text-xs text-orange-500 ml-2">
                      Requires Multi-Table Review to be enabled
                    </span>
                  )}
                </div>

                {configData.SprayEnabled && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Models to Use (1-5)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={configData.SprayModelsCount}
                      onChange={(e) => handleConfigChange('SprayModelsCount', parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {configData.SprayEnabled && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model Priority (Drag to Reorder)
                    </label>
                    <div className="border rounded-md p-2 bg-white">
                    {configData.SprayModelsPriority.length === 0 ? (
                      <div className="text-sm text-gray-500 py-2 text-center">
                        No models selected for Spray. Click "Add Model" to add models.
                      </div>
                    ) : (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <div className="text-sm text-gray-600 mb-2">
                          The top {configData.SprayModelsCount} model{configData.SprayModelsCount !== 1 ? 's' : ''} (highlighted in green) will be used when Spray is enabled.
                        </div>
                        <Droppable droppableId="spray-models-list">
                          {(provided) => (
                            <ul
                              className="space-y-2"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {configData.SprayModelsPriority.map((modelName, index) => {
                                const model = availableModels.find(m => m.name === modelName);
                                return (
                                  <Draggable key={modelName} draggableId={modelName} index={index}>
                                    {(provided) => (
                                      <li
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`flex items-center p-2 rounded-md ${
                                          index < configData.SprayModelsCount 
                                            ? 'bg-green-50 border border-green-200' 
                                            : 'bg-gray-50 opacity-75'
                                        }`}
                                      >
                                        <div {...provided.dragHandleProps} className="px-2 text-gray-500">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="9" cy="5" r="1" />
                                            <circle cx="9" cy="12" r="1" />
                                            <circle cx="9" cy="19" r="1" />
                                            <circle cx="15" cy="5" r="1" />
                                            <circle cx="15" cy="12" r="1" />
                                            <circle cx="15" cy="19" r="1" />
                                          </svg>
                                        </div>
                                        <div className="flex-1 ml-2">
                                          {model ? `${model.name} (${model.Platform} - ${model.Model})` : modelName}
                                          {index < configData.SprayModelsCount && (
                                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                              Active
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => {
                                            const newPriority = configData.SprayModelsPriority.filter(
                                              (_, i) => i !== index
                                            );
                                            handleConfigChange('SprayModelsPriority', newPriority);
                                          }}
                                          className="text-red-500 hover:text-red-700 px-2"
                                        >
                                          Remove
                                        </button>
                                      </li>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </ul>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                      <div className="mt-4 flex justify-between">
                        <button
                          onClick={() => {
                            const availableForSpray = availableModels
                              .filter(model => !configData.SprayModelsPriority.includes(model.name))
                              .slice(0, 1);
                              
                            if (availableForSpray.length > 0) {
                              const newPriority = [...configData.SprayModelsPriority, availableForSpray[0].name];
                              handleConfigChange('SprayModelsPriority', newPriority);
                            } else {
                              alert('All available models have been added to the priority list.');
                            }
                          }}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                          disabled={configData.SprayModelsPriority.length >= availableModels.length}
                        >
                          + Add Model
                        </button>
                        <button
                          onClick={() => {
                            // Implement a modal to select multiple models at once
                            const selectedModels = availableModels
                              .filter(model => !configData.SprayModelsPriority.includes(model.name))
                              .slice(0, 3)
                              .map(model => model.name);
                            
                            if (selectedModels.length > 0) {
                              const newPriority = [...configData.SprayModelsPriority, ...selectedModels];
                              handleConfigChange('SprayModelsPriority', newPriority);
                            } else {
                              alert('All available models have been added to the priority list.');
                            }
                          }}
                          className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                        >
                          Add Multiple Models
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Models will be tried in the order listed above when Spray is enabled.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-3">Preference Learning Settings</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Initial Query Threshold
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={configData.initialQueryThreshold}
                      onChange={(e) => handleConfigChange('initialQueryThreshold', parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of follow-ups before first learning run
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Retrigger Threshold
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={configData.retriggerThreshold}
                      onChange={(e) => handleConfigChange('retriggerThreshold', parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      New follow-ups needed to re-analyze preferences
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      History Query Count
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="50"
                      value={configData.historyQueryCount}
                      onChange={(e) => handleConfigChange('historyQueryCount', parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of previous queries to analyze
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Learning Schedule Interval
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="1440"
                      value={configData.learningScheduleMinutes}
                      onChange={(e) => handleConfigChange('learningScheduleMinutes', parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How often the scheduled job runs (in minutes)
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-3">Data Download Settings</h4>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Rows for Download
                  </label>
                  <input
                    type="number"
                    min="1000"
                    max="1000000"
                    value={configData.MaxRows}
                    onChange={(e) => handleConfigChange('MaxRows', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum number of rows that can be downloaded (1,000 - 1,000,000)
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-3">Demo Mode</h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800 mb-3">
                    When enabled, all data will be anonymized for demonstrations:
                  </p>
                  <ul className="text-sm text-yellow-700 ml-4 mb-3 list-disc">
                    <li>Text values replaced with codes (STR-XXX)</li>
                    <li>Numbers multiplied by random factors (0.35-1.95)</li>
                    <li>Relationships between data preserved</li>
                  </ul>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={configData.demoMode || false}
                      onChange={(e) => handleConfigChange('demoMode', e.target.checked, 'core')}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Enable Demo Mode
                    </label>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-3">Support Settings</h4>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Emails
                </label>
                <div className="space-y-2">
                  {configData.Support.map((email, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const newEmails = [...configData.Support];
                          newEmails[index] = e.target.value;
                          handleSupportEmailsChange(newEmails);
                        }}
                        className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => {
                          const newEmails = configData.Support.filter((_, i) => i !== index);
                          handleSupportEmailsChange(newEmails);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newEmails = [...configData.Support, ''];
                      handleSupportEmailsChange(newEmails);
                    }}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    + Add Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DBIConfigManagement;